/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { LoadingButton } from '@mui/lab';
import { useLocation, useSearchParams } from 'react-router-dom';
import InvoiceStatus from 'utils/styles/InvoiceStatus.styles';
import { ReactComponent as PdfIcon } from 'assets/icons/pdf.svg';
import { useBaoDispatch, useBaoSelector } from 'utils/hooks/redux';
import { setPage, storeInvoicesInfo } from 'store/invoices/invoicesSlice';
import { statusType } from 'utils/constants/invoiceStatus';
import { ADMIN, SALES_REP, SUPER_ADMIN } from 'utils/constants/roles';
import PayInvoicePopup from 'organisms/PayInvoicePopup/PayInvoicePopup';
import { setInvoice } from 'store/payInvoice/payInvoiceSlice';
import { useIndiaInvoiceRedirect } from 'utils/hooks/useIndiaInvoiceRedirect';
import { currencyFormatter } from 'utils/helpers/currencyFormatter';
import { formatDate } from 'utils/helpers/dates';
import { snakeCase } from 'lodash-es';
import { ActionContainerStyled, PayButtonStyled } from './InvoicesTable.styles';
import { handleDownloadInvoice } from './helpers';
import { StyledCellText } from 'molecules/Table/styles/Text.styles';
import {
  Data,
  InvoicesTableProps,
  Order,
  Row,
} from 'organisms/InvoicesTable/types';
import Link from 'atoms/Link/Link';
import {
  StyledCellContainer,
  StyledTableContainer,
} from 'molecules/Table/styles';
import { StyledTablePagination } from 'molecules/Table/styles/Pagination.styles';
import Table from 'molecules/Table';
import PaginationActions from 'molecules/Table/TablePagination/Actions';
import Services from 'organisms/InvoicesTable/Services/Services';
import { InvoiceTimeLogModal } from 'molecules/TimeLogTable/InvoiceTimeLogModal';
import InvoicePreview from 'molecules/InvoicePreview';
import { palette } from 'utils/styles/variables';
import Modal from 'molecules/Modal';
import { ModalActions, ModalCloseButton } from 'molecules/Modal/Modal.styles';

export const InvoicesTable = ({
  search,
  status,
  currency,
  dateRange,
  onError,
  companyColumn = false,
  company,
  period,
  customer,
  invoiceType,
  projectIds,
  setHeight,
  contractType,
}: InvoicesTableProps) => {
  // const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const invoices = useBaoSelector((state) => state.invoices);
  const { value: user } = useBaoSelector((state) => state.user);
  const payInvoice = useBaoSelector((state) => state.payInvoice);
  const { currentCompany } = useBaoSelector((state) => state.common);

  const {
    isIndiaRegion,
    ensureIsIndiaRegion,
    redirectToInvoiceUrl,
    redirectingId,
  } = useIndiaInvoiceRedirect();

  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof Data[0]>('date');
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [invoicesRows, setInvoicesRow] = useState<Row[]>([]);
  const [showServices, setShowServices] = useState(0);
  const [loader, setLoader] = useState(true);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [activeInvoice, setActiveInvoice] = useState<Row | null>(null);
  const [activeInvoiceAdjustment, setActiveInvoiceAdjustment] =
    useState<Row | null>(null);
  const [invoicePreview, setInvoicePreview] = useState<string | null>(null);
  const [paymentState, setPaymentState] = useState<{
    open: boolean;
    id: string | null;
    state: 'succeeded' | 'canceled' | null;
    message: string | null;
  }>({
    open: false,
    id: null,
    state: null,
    message: null,
  });

  const isCustomer =
    user.userType !== SUPER_ADMIN &&
    user.userType !== ADMIN &&
    user.userType !== SALES_REP;

  const dispatch = useBaoDispatch();

  const searchParamsGet = new URLSearchParams(useLocation().search);
  const statusGet = searchParamsGet.get('status') as statusType;

  const columns = useMemo(
    () => [
      {
        key: 'date',
        label: 'Date',
        sortable: true,
        width: companyColumn ? '14%' : '18%',
        render: ({ date }: Row) => (
          <StyledCellText
            $textType="bold"
            sx={{ fontSize: '0.813rem !important' }}
          >
            {date}
          </StyledCellText>
        ),
      },
      {
        key: 'invoiceQbNumber',
        label: 'Invoice #',
        sortable: false,
        width: companyColumn ? '14%' : '20%',
        render: ({ invoiceQbNumber, invoiceQbId }: Row) => (
          <StyledCellText
            $color={palette.primary.main}
            sx={{
              display: 'inline',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
            onClick={(event) => {
              event?.preventDefault();
              event?.stopPropagation();

              setInvoicePreview(invoiceQbId);
              document.body.setAttribute('id', 'body');
            }}
          >
            #{invoiceQbNumber}
          </StyledCellText>
        ),
      },
      ...(companyColumn
        ? [
            {
              key: 'company',
              label: 'Company',
              sortable: true,
              width: '25%',
              render: ({ company: companyName, companyHsId }: Row) => (
                <Link to={`/company/${companyHsId}`} $textDecoration="none">
                  <StyledCellText $textType="link">
                    {companyName}
                  </StyledCellText>
                </Link>
              ),
            },
          ]
        : []),
      {
        key: 'invoiceTotal',
        label: 'Invoice Total',
        sortable: true,
        width: companyColumn ? '15%' : '174px',
        render: ({ invoiceTotal, currency: rowCurrency }: Row) => (
          <StyledCellContainer
            $right
            $vStack
            $pr={companyColumn ? '20px' : '60px'}
          >
            <StyledCellText
              sx={companyColumn ? {} : { width: '114px', textAlign: 'end' }}
            >
              {invoiceTotal
                ? currencyFormatter(invoiceTotal, rowCurrency)
                : currencyFormatter(0, rowCurrency)}
            </StyledCellText>
          </StyledCellContainer>
        ),
      },
      {
        key: 'invoiceType',
        label: 'Type of Invoice',
        sortable: true,
        width: '8%',
        render: ({ invoiceType: _invoiceType }: Row) => (
          <StyledCellText>{_invoiceType}</StyledCellText>
        ),
      },
      {
        key: 'status',
        label: 'Status',
        greyText: 'Due date',
        sortable: false,
        width: companyColumn ? '15%' : '20%',
        render: ({
          status: invoiceStatus,
          dueDate,
          paidDate,
          amountDue,
          discountAmount,
        }: Row) => (
          <StyledCellContainer $vStack $display="inline">
            <InvoiceStatus>
              {invoiceStatus.state === 'open' &&
                invoiceStatus.days > 0 &&
                `OPEN DUE ${invoiceStatus.days} ${
                  invoiceStatus.days === 1 ? 'DAY' : 'DAYS'
                }`}

              {invoiceStatus.state === 'open' &&
                invoiceStatus.days === 0 &&
                'OPEN DUE TODAY'}

              {invoiceStatus.state === 'overdue' &&
                `OVERDUE ${invoiceStatus.days} ${
                  invoiceStatus.days === 1 ? 'DAY' : 'DAYS'
                }`}

              {invoiceStatus.state === 'paid' &&
                `PAID ${formatDate(paidDate, { time: '' })}${
                  amountDue === 0 && discountAmount ? ' by discount' : ''
                }`?.trim()}

              {invoiceStatus.state === 'payment_pending' && 'PENDING'}

              {invoiceStatus.state === 'pending' &&
                invoiceStatus.state.toUpperCase()}

              {invoiceStatus.state === 'voided' &&
                invoiceStatus.state.toUpperCase()}

              {invoiceStatus.state === 'past_due' &&
                invoiceStatus.state.toUpperCase()}
            </InvoiceStatus>

            <StyledCellText $textType="grey">{dueDate}</StyledCellText>
          </StyledCellContainer>
        ),
      },
      {
        key: 'actions',
        label: '',
        sortable: false,
        render: (row: Row) => (
          <ActionContainerStyled>
            {user.userType !== SUPER_ADMIN &&
              user.userType !== ADMIN &&
              user.userType !== SALES_REP &&
              row.status.state !== 'voided' &&
              row.status.state !== 'payment_pending' &&
              row.status.state !== 'paid' &&
              !companyColumn && (
                <PayButtonStyled
                  variant="contained"
                  size="small"
                  disabled={
                    redirectingId === row.invoiceAccountId ||
                    (isIndiaRegion && !row.invoiceAccountId)
                  }
                  onClick={async (event) => {
                    event.stopPropagation();
                    if (await ensureIsIndiaRegion()) {
                      const result = await redirectToInvoiceUrl(
                        row.invoiceAccountId
                      );
                      if (!result.ok) {
                        setPaymentState({
                          open: true,
                          id: row.invoiceQbNumber,
                          state: 'canceled',
                          message: result.message,
                        });
                      }
                      return;
                    }
                    dispatch(setInvoice(row));
                  }}
                  color="secondary"
                >
                  Pay
                </PayButtonStyled>
              )}

            <LoadingButton
              loading={downloading === row.invoiceQbId}
              sx={{ p: 0, '&:hover': { background: 'transparent' } }}
              onClick={async (event) => {
                event.stopPropagation();
                try {
                  setDownloading(row.invoiceQbId);
                  await handleDownloadInvoice([row.invoiceQbId]);
                } finally {
                  setDownloading(null);
                }
              }}
            >
              {downloading !== row.invoiceQbId && (
                <PdfIcon height={24} width={24} />
              )}
            </LoadingButton>
          </ActionContainerStyled>
        ),
      },
    ],
    [
      companyColumn,
      downloading,
      redirectingId,
      isIndiaRegion,
      ensureIsIndiaRegion,
      redirectToInvoiceUrl,
    ]
  );

  useEffect(() => {
    const payId = localStorage.getItem('pay');
    const paymentStatus = searchParams.get('checkout_status') as
      | 'succeeded'
      | 'canceled';
    if (!payInvoice.open && payId && !paymentState.open) {
      const prevOpenInv = invoices.rows.find((inv) => inv.id === payId);

      if (paymentStatus && prevOpenInv) {
        setPaymentState({
          open: true,
          id: prevOpenInv.invoiceQbNumber,
          state: paymentStatus,
          message:
            paymentStatus === 'succeeded'
              ? 'Payment successful! Your invoice has been paid.'
              : 'Payment failed. Please try again or use a different payment method.',
        });
        return;
      }

      if (prevOpenInv) {
        dispatch(setInvoice(prevOpenInv));
      }
    }
  }, [payInvoice, invoices]);

  useEffect(() => {
    if (isCustomer && !currentCompany) return;

    fetchPageData();
  }, [
    status,
    location,
    invoices.page,
    rowsPerPage,
    dispatch,
    searchParams,
    company,
    orderBy,
    order,
    search,
    currency,
    dateRange,
    period,
    customer,
    invoiceType,
    statusGet,
    currentCompany,
    contractType,
    projectIds,
  ]);

  useEffect(() => {
    if (invoices.rows && invoices.rows.length > 0) {
      setLoader(false);
    }

    if (
      invoices.rows &&
      invoices.rows.length === 0 &&
      Number(searchParams.get('page')) > 0 &&
      invoices.totalCount > 0
    ) {
      // searchParams.set('page', '0');
      // navigate(`?${searchParams}`);
    }
    setInvoicesRow(invoices.rows);

    setTimeout(() => {
      if (invoices && invoices.totalCount === 0) {
        setLoader(false);
      }
    }, 5000);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices]);

  useEffect(() => {
    setShowServices(0);
  }, [period, status]);

  useEffect(() => {
    setShowServices(0);
    dispatch(setPage(0));
  }, [
    search,
    status,
    dateRange,
    currency,
    invoiceType,
    contractType,
    projectIds,
  ]);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data[0]
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setLoader(true);
    setTimeout(() => {
      // searchParams.set('page', newPage.toString());
      // navigate(`?${searchParams}`);
      dispatch(setPage(newPage));
    }, 500);
  };

  const handleChangeRowsPerPage = (rows: number) => {
    setRowsPerPage(rows);
    // searchParams.set('page', '0');
    // navigate(`?${searchParams}`);
    dispatch(setPage(0));
  };

  const handleRowClick = useCallback(
    (event, row) => {
      if (window.getSelection()?.type === 'Range') return;

      if (showServices === row.id) {
        setShowServices(0);
        if (setHeight) {
          setHeight(false);
        }
      } else {
        setShowServices(row.id);
        if (setHeight) {
          setHeight(true);
        }
      }
    },
    [showServices]
  );

  const fetchPageData = () => {
    const newPage = Number(searchParams.get('page')) || invoices.page || 0;
    let isAll = false;
    if (
      statusGet &&
      statusGet === 'all' &&
      location.state &&
      location.state.type &&
      location.state.type === 'all'
    ) {
      isAll = true;
    }

    dispatch(
      storeInvoicesInfo({
        customer,
        period,
        newPage,
        rowsPerPage,
        status: isAll ? 'all' : status,
        sort: `sort[${snakeCase(orderBy)}]=${order}`,
        currency,
        company,
        search,
        dateRange,
        invoiceType,
        projectIds,
        contractType: contractType || undefined,
      })
    );
  };

  const handleOpenLog = useCallback((invoice: Row) => {
    setActiveInvoice(invoice);
    document.body.setAttribute('id', 'body');
  }, []);

  const handleCloseLog = useCallback(() => {
    setActiveInvoice(null);
    document.body.setAttribute('id', '');
  }, []);

  const handleOpenAdjustment = useCallback((invoice: Row) => {
    setActiveInvoiceAdjustment(invoice);
    document.body.setAttribute('id', 'body');
  }, []);

  const handleCloseAdjustment = useCallback(() => {
    setActiveInvoiceAdjustment(null);
    document.body.setAttribute('id', '');
  }, []);

  const handleClosePreview = useCallback(() => {
    setInvoicePreview(null);
    document.body.setAttribute('id', '');
  }, []);

  const rowDetailsRenderer = useCallback(
    (row) => (
      <Services
        row={row}
        onOpenLog={handleOpenLog}
        onOpenAdjustment={handleOpenAdjustment}
      />
    ),
    []
  );

  const handleClosePaymentState = useCallback(() => {
    setPaymentState({
      open: false,
      id: null,
      state: null,
      message: null,
    });

    localStorage.removeItem('pay');
    searchParams.delete('checkout_status');
    setSearchParams(searchParams);
  }, [searchParams]);

  return (
    <>
      <StyledTableContainer>
        <Table
          idKey="id"
          loading={loader || invoices.isLoading}
          collapsible
          collapsedRow={showServices}
          order={order}
          orderBy={orderBy}
          onRequestSort={handleRequestSort}
          columns={columns}
          data={invoicesRows || []}
          onRowClick={handleRowClick}
          rowDetailsRenderer={rowDetailsRenderer}
        />
        <StyledTablePagination
          labelDisplayedRows={() => null}
          rowsPerPageOptions={[]}
          count={invoices.totalCount ? invoices.totalCount : 0}
          rowsPerPage={rowsPerPage}
          page={invoices.page}
          onPageChange={handleChangePage}
          ActionsComponent={(props) => (
            <PaginationActions
              {...props}
              labelRowsPerPage="Items per page"
              rowsPerPageOptions={[20, 50, 100, 200]}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        />
      </StyledTableContainer>

      {activeInvoice && (
        <InvoiceTimeLogModal invoice={activeInvoice} onClose={handleCloseLog} />
      )}

      {activeInvoiceAdjustment && (
        <InvoiceTimeLogModal
          invoice={activeInvoiceAdjustment}
          onClose={handleCloseAdjustment}
        />
      )}

      {invoicePreview && (
        <InvoicePreview id={invoicePreview} onClose={handleClosePreview} />
      )}

      <PayInvoicePopup onAfterClose={fetchPageData} onError={onError} />

      <Modal
        open={paymentState.open}
        maxWidth="sm"
        title={`Invoice #${paymentState.id} payment`}
        subtitle={<span>{paymentState.message}</span>}
        onClose={handleClosePaymentState}
        sx={{ zIndex: 33333 }}
      >
        <ModalActions>
          <ModalCloseButton
            autoFocus
            size="medium"
            variant="outlined"
            onClick={handleClosePaymentState}
          >
            Close
          </ModalCloseButton>
        </ModalActions>
      </Modal>
    </>
  );
};

export default InvoicesTable;
